import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getArticle } from "../data/articles";

const SITE = "https://www.ah-jingroup.com";
const ES_PATHS = /^\/(abrir-broker-latam|insights\/como-|insights\/.*-latam)/;

const DEFAULT_TITLE = "Ah-Jin Group | Brokerage & Prop Firm Launch Consultancy — Incorporation, Licensing, Platforms";
const DEFAULT_DESC = "Ah-Jin Group — brokerage and prop-firm launch consultancy: incorporation, licensing, trading platforms, liquidity, CRM, payments and compliance. Book a $250 strategy call.";
const PAGE_META = {
  "/clients": {
    title: "Our Clients — Brokerages & Prop Firms Launched by Ah-Jin Group | Ah-Jin Group",
    desc: "Every brokerage, prop firm and investment company Ah-Jin Group has taken from first document to go-live: who they are, what we delivered and what they say.",
  },
  "/pricing": {
    title: "Pricing — Brokerage & Prop Firm Launch Packages from $6,500 | Ah-Jin Group",
    desc: "Transparent pricing for incorporation, licensing, brokerage launch and prop-firm launch. Fixed fees, itemised third-party costs, and a $250 strategy call credited toward your engagement.",
  },
  "/crm": {
    title: "Forex CRM & Back Office Setup — FXBO, Client Portal, IB Tree | Ah-Jin Group",
    desc: "We select, negotiate and integrate your brokerage CRM and back office: client portal, KYC, IB/affiliate tree, payment integration and reporting — set up to go live with your platform.",
  },
  "/payments": {
    title: "Payments & Banking for Brokers & Prop Firms — Corporate Bank Accounts, Card Processing, Crypto | Ah-Jin Group",
    desc: "Corporate bank and EMI accounts, card payment processing (acquirers, PSPs, 3-DS, chargebacks) and crypto gateways for new forex brokerages and prop trading firms — opened, negotiated and connected to your platform.",
  },
  "/legal-compliance": {
    title: "Compliance Support for Brokers — AML/KYC Policies, MLRO & DMLRO, Regulation | Ah-Jin Group",
    desc: "Compliance support for forex brokerages and prop trading firms through our associates: AML and KYC policies, MLRO / DMLRO cover, client agreements, risk warnings and regulator liaison — so your launch is bank-ready and audit-ready.",
  },
  "/lp-bridge": {
    title: "Liquidity Providers & Bridges for Forex Brokers — STP, Pricing, Deposits | Ah-Jin Group",
    desc: "We connect your brokerage to liquidity: LP selection, bridge/aggregator setup, markups, and deposit negotiation — with real pricing comparisons before you sign.",
  },
  "/trading-platforms": {
    title: "Trading Platform Setup — MT5 White Label, cTrader, Match-Trader | Ah-Jin Group",
    desc: "MT5 white label, cTrader and Match-Trader setup for new brokers: platform choice, licence costs, server configuration and integration with liquidity, CRM and payments.",
  },
};

const PAGE_KW = {
  "/clients": "brokerage clients, prop firm clients, forex broker launch case studies, brokerage consultancy reviews",
  "/pricing": "forex brokerage setup cost, prop firm setup cost, how much does it cost to start a brokerage, company incorporation price, forex licence cost, white label broker price, $250 strategy call",
  "/crm": "forex CRM, broker back office, FXBO, client portal, IB system, affiliate system broker, KYC onboarding, brokerage CRM integration",
  "/payments": "corporate bank account for brokers, corporate banking forex, EMI account, card payment processing brokers, card payment providers, high-risk merchant account, PSP for forex brokers, payment gateway integration, crypto payment gateway, payment processing and banking support",
  "/legal-compliance": "compliance support for brokers, AML compliance, KYC AML policy, outsourced MLRO, DMLRO, MLRO services, regulatory reporting, financial regulation, MiFID compliance, broker regulation, compliance through associates",
  "/lp-bridge": "liquidity provider for brokers, forex liquidity, liquidity bridge, aggregator, STP broker, ECN broker, prime of prime",
  "/trading-platforms": "MT5 white label, MetaTrader 5, cTrader white label, Match-Trader, trading platform for brokers, prop firm platform, trading technology",
};

const setMeta = (name, content) => {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (key, attrs) => {
  let el = document.head.querySelector(`link[data-route-meta="${key}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("data-route-meta", key);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

/* Per-route canonical + hreflang + robots — one source of truth for every page */
const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
    const url = `${SITE}${path === "/" ? "/" : path}`;
    const isAdmin = path.startsWith("/admin") || path.startsWith("/payment");
    setLink("canonical", { rel: "canonical", href: url });
    const pm = PAGE_META[path];
    if (pm) {
      document.title = pm.title;
      setMeta("description", pm.desc);
      if (PAGE_KW[path]) setMeta("keywords", PAGE_KW[path]);
    } else if (path === "/") {
      document.title = DEFAULT_TITLE;
      setMeta("description", DEFAULT_DESC);
    }
    // social cards follow the page
    document.head.querySelector('meta[property="og:url"]')?.setAttribute("content", url);
    if (pm || path === "/") {
      document.head.querySelector('meta[property="og:title"]')?.setAttribute("content", pm ? pm.title : DEFAULT_TITLE);
      document.head.querySelector('meta[property="og:description"]')?.setAttribute("content", pm ? pm.desc : DEFAULT_DESC);
    }
    if (ES_PATHS.test(path)) {
      setLink("hl-es", { rel: "alternate", hreflang: "es", href: url });
      setLink("hl-x", { rel: "alternate", hreflang: "x-default", href: url });
      document.head.querySelector('link[data-route-meta="hl-en"]')?.remove();
    } else {
      setLink("hl-en", { rel: "alternate", hreflang: "en", href: url });
      setLink("hl-es", { rel: "alternate", hreflang: "es", href: `${url}${url.includes("?") ? "&" : "?"}lang=es` });
      setLink("hl-x", { rel: "alternate", hreflang: "x-default", href: url });
    }
    // breadcrumbs for rich results (Home > Section > Page)
    document.getElementById("breadcrumb-jsonld")?.remove();
    if (path !== "/" && !isAdmin) {
      const segs = path.split("/").filter(Boolean);
      const items = [{ "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" }];
      let acc = "";
      segs.forEach((sg, i) => {
        acc += `/${sg}`;
        const art = segs[0] === "insights" && i === 1 ? getArticle(sg) : null;
        const human = sg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const name = art ? art.title : i === segs.length - 1 && pm ? pm.title.split(" — ")[0].split(" | ")[0] : human;
        items.push({ "@type": "ListItem", position: i + 2, name, item: SITE + acc });
      });
      const bc = document.createElement("script");
      bc.type = "application/ld+json";
      bc.id = "breadcrumb-jsonld";
      bc.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items });
      document.head.appendChild(bc);
    }
    let robots = document.head.querySelector('meta[data-route-meta="robots"]');
    if (isAdmin) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("data-route-meta", "robots");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.content = "noindex, nofollow";
    } else if (robots) {
      robots.remove();
    }
  }, [pathname]);
  return null;
};

export default RouteMeta;
